/**
 * Leaderboard avatar: FriendAvatar + optional profile badge (same pattern as Friends BaseCard).
 * Pass showProfileBadge={false} only if the layout cannot fit the corner chip.
 *
 * When a PlayerRanksProvider is mounted above (the global WC leaderboard only — see
 * specs/rank-on-social-screens §7), the rank crest takes the bottom-right corner and the
 * profile chip yields it rather than stacking: the chip is that player's photo, already
 * redundant beside the avatar it sits on, and the row is too dense for both.
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import { isAvatarUrl, normalizeAvatarImageUri } from '../../../src/create/helpers/createUi.helper';
import { webImageAltProfilePhoto } from '../../../src/shared/helpers/imageWebAlt.helper';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { getAssetUrl, ASSET_PATHS } from '../../../constants/assets';
import { useTheme } from '../../../src/theme/useTheme';
import { useUserProfileOptional } from '../../../src/shared/contexts/UserProfileContext';
import { usePlayerRankLookup } from '../../../src/economy/contexts/PlayerRanksContext';
import { PlayerRankBadge } from '../../../src/economy/components/PlayerRankBadge/PlayerRankBadge';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';
import { getWorldCupLeaderboardStyles } from './WorldCupLeaderboard.styles';
import {
  getLeaderboardCrestSize,
  getLeaderboardUserAvatarStyles,
} from './LeaderboardUserAvatar.styles';

const DEFAULT_MONKEY_URI = getAssetUrl(ASSET_PATHS.AVATARS.monkey.idle);

type LeaderboardAvatarVariant = 'row' | 'podiumLarge' | 'podiumSmall';

export const LeaderboardUserAvatar: React.FC<{
  entry: LeaderboardEntry;
  variant: LeaderboardAvatarVariant;
  styles: ReturnType<typeof getWorldCupLeaderboardStyles>;
  showProfileBadge?: boolean;
}> = ({ entry, variant, styles, showProfileBadge = true }) => {
  const theme = useTheme();
  const profile = useUserProfileOptional()?.profile ?? null;
  // For the viewer's OWN row, render their live profile avatar so a just-changed look shows
  // immediately, instead of the value baked into the (server-cached) leaderboard slice.
  const useLiveAvatar = entry.isCurrentUser === true && profile != null;
  const liveAvatarId = useLiveAvatar ? profile.avatarId : entry.avatarId;
  const liveAvatarConfig = useLiveAvatar ? profile.avatarConfig : entry.avatarConfig;
  const size = variant === 'row' ? 34 : variant === 'podiumLarge' ? 76 : 60;
  const isPodium = variant !== 'row';
  // Null unless a PlayerRanksProvider is mounted above (i.e. only the global WC leaderboard).
  const playerRank = usePlayerRankLookup()(entry.userId);
  const variantStyles = useMemo(
    () => getLeaderboardUserAvatarStyles(theme, { size, isPodium }),
    [theme, size, isPodium],
  );
  const slotStyle =
    variant === 'row'
      ? styles.avatarSlotRow
      : variant === 'podiumLarge'
        ? styles.avatarSlotPodiumLarge
        : styles.avatarSlotPodiumSmall;

  if (entry.userId === MONKEY_USER_ID) {
    return (
      <View style={slotStyle}>
        <Image
          source={{ uri: DEFAULT_MONKEY_URI }}
          style={variantStyles.orgMonkeyImage}
          contentFit="cover"
          cachePolicy="memory-disk"
          accessibilityLabel={webImageAltProfilePhoto(entry.name)}
          alt={webImageAltProfilePhoto(entry.name)}
        />
      </View>
    );
  }

  const hasProfilePic = entry.avatarUrl != null && isAvatarUrl(entry.avatarUrl);

  return (
    <View style={slotStyle}>
      <FriendAvatar
        avatarId={
          liveAvatarId != null && liveAvatarId !== ''
            ? normalizeDisplayAvatarId(liveAvatarId)
            : undefined
        }
        avatarConfig={liveAvatarConfig}
        fallbackUrl={hasProfilePic ? entry.avatarUrl : undefined}
        size={size}
        nameForInitials={entry.name}
        avatarRounded
      />
      {showProfileBadge && playerRank == null ? (
        <View
          style={
            isPodium
              ? [styles.profileBadge, variantStyles.profileBadgePodiumOverride]
              : styles.profileBadge
          }
        >
          {hasProfilePic ? (
            <Image
              source={{ uri: normalizeAvatarImageUri(entry.avatarUrl ?? '') }}
              style={styles.profileBadgeImage}
              contentFit="cover"
              cachePolicy="memory-disk"
              accessibilityLabel={webImageAltProfilePhoto(entry.name)}
              alt={webImageAltProfilePhoto(entry.name)}
            />
          ) : (
            <View style={styles.profileBadgeInitials}>
              <Text
                style={
                  isPodium
                    ? [
                        styles.profileBadgeInitialsText,
                        variantStyles.profileBadgeInitialsFontPodiumOverride,
                      ]
                    : styles.profileBadgeInitialsText
                }
              >
                {entry.initials}
              </Text>
            </View>
          )}
        </View>
      ) : null}
      {playerRank != null ? (
        <PlayerRankBadge
          rankIndex={playerRank.rank}
          size={getLeaderboardCrestSize(size)}
          style={variantStyles.rankCrest}
        />
      ) : null}
    </View>
  );
};
